import { PercentageExpProgress } from './percentage-exp-progress'
import { UnknownLevelExpTracker } from './unknown-level-exp'
import { SessionMapTracker } from './session-map'
import { metricKeys, SESSION_JOURNEY_VERSION, type AccountingFrame, type GameEvent, type GameStateSnapshot, type LiveRateSnapshot, type RankSubmissionDraft, type SessionAggregate, type SessionIngestResult, type SessionJourney, type SessionRecord, type ValidatedObservation } from '@/domain/types'
import type { GamePreset } from '@/presets/types'
import { EfficiencySegmentTracker } from './efficiency-segment-tracker'
import { isRankPresetId } from '@/domain/types'
import { SessionExpCounter } from './session-exp-counter'
import { SessionExpRateTracker } from './session-exp-rate'
import { SessionExpHistory } from './session-exp-history'
import { SessionExpReadout } from './session-exp-readout'
import { SessionJourneyRecorder } from './session-journey'
import { TrustedMetricLedger } from './trusted-metric-ledger'
import type { PerformanceTier } from './performance-settings'
import type { RankSourceRestriction } from './rank-capture'

const emptyCoverage = () => Object.fromEntries(metricKeys.map((key) => [key, 0])) as SessionAggregate['coverage']
export const SESSION_ACCOUNTING_VERSION = 11
const HOUR_MS = 3_600_000

export class SessionEngine {
  private readonly percentageProgress = new PercentageExpProgress()
  private readonly mapTracker = new SessionMapTracker()
  private mapBoundaryAt = -Infinity
  private partialExp: UnknownLevelExpTracker | null = null
  private knownExpStartedActiveAt: number | null = null
  private partialKnownGain = 0
  private partialKnownDuration = 0
  private record: SessionRecord | null = null
  private pauseStartedAt: number | null = null
  private accumulatedPauseMs = 0
  private latestState: GameStateSnapshot | null = null
  private captureGeneration = 0
  private readonly ledger: TrustedMetricLedger
  private readonly expCounter: SessionExpCounter
  private readonly expRateTracker = new SessionExpRateTracker()
  private readonly expHistory = new SessionExpHistory()
  private readonly expReadout = new SessionExpReadout(
    (display, invalidated, uncommitted) => this.expHistory.ingest(display, invalidated, 'recovery', uncommitted),
    at => this.expHistory.breakContinuity(at),
  )
  private readonly journeyRecorder = new SessionJourneyRecorder()
  private readonly rankTracker: EfficiencySegmentTracker | null
  private pendingRankSubmissions: RankSubmissionDraft[] = []
  private latestAccounting: AccountingFrame | null = null
  private readonly confirmedLosses = new Set<string>()
  private rankPerformanceRestricted = false
  private rankSourceRestricted: RankSourceRestriction | null = null
  private percentageRankingGap = false

  constructor(private readonly preset: GamePreset) {
    this.ledger = new TrustedMetricLedger(preset)
    this.expCounter = new SessionExpCounter((display, invalidated, uncommitted) => {
      this.expHistory.ingest(display, invalidated, 'session', uncommitted)
    }, preset.id === 'gcw')
    this.rankTracker = isRankPresetId(preset.id) ? new EfficiencySegmentTracker(preset.version, preset.id) : null
  }

  start(
    profileId: string,
    now = Date.now(),
    initialState: GameStateSnapshot | null = null,
    captureGeneration = 0,
  ): SessionRecord {
    if (this.record && this.record.status !== 'ended') throw new Error('A record is already active')
    this.percentageProgress.reset()
    if (initialState?.exp.percentageOnly && initialState.exp.estimated && initialState.exp.absolute === null) {
      this.percentageProgress.observe({ value: { ...initialState.exp, estimated: true }, level: initialState.level,
        sourceAt: now, deliveredAt: now, confidence: initialState.confidence.exp, confirmation: 'consensus' })
    }
    this.pauseStartedAt = null
    this.accumulatedPauseMs = 0
    this.partialExp = initialState?.level === null && initialState.exp.absolute !== null && initialState.exp.ratio !== null
      ? new UnknownLevelExpTracker(initialState, now) : null
    this.knownExpStartedActiveAt = null
    this.partialKnownGain = 0
    this.partialKnownDuration = 0
    this.latestState = initialState
    this.mapTracker.reset(initialState?.mapName ?? null)
    this.mapBoundaryAt = now
    this.latestAccounting = null
    this.captureGeneration = captureGeneration
    this.pendingRankSubmissions = []
    this.percentageRankingGap = initialState?.exp.estimated === true
    this.confirmedLosses.clear()
    this.rankPerformanceRestricted = false
    this.rankSourceRestricted = null
    this.rankTracker?.reset()
    this.ledger.reset(now, captureGeneration, initialState)
    this.expRateTracker.reset()
    this.expHistory.reset(now)
    this.record = {
      id: crypto.randomUUID(),
      presetId: this.preset.id,
      presetVersion: this.preset.version,
      profileId,
      status: 'running',
      startedAt: now,
      activeDurationMs: 0,
      sessionAccountingVersion: SESSION_ACCOUNTING_VERSION,
      journeyVersion: SESSION_JOURNEY_VERSION,
      aggregate: this.emptyAggregate(),
      ...(initialState ? { startState: initialState, endState: initialState } : {}),
    }
    this.expCounter.reset(initialState, now)
    this.expReadout.reset()
    if (initialState) this.expReadout.ingest(this.expCounter, initialState, undefined, [], .62, at => this.activeDuration(at))
    const baseline = this.expCounter.snapshot()
    if (initialState && baseline.sourceAt !== null) this.expHistory.ingest({ ...baseline, sourceAt: now })
    const record = this.update({}, now)
    this.journeyRecorder.start(record, initialState)
    return record
  }

  pause(now = Date.now()): SessionRecord {
    this.assertStatus('running')
    this.percentageProgress.breakContinuity()
    this.partialExp?.breakContinuity(now)
    this.expHistory.breakContinuity(now, 'paused')
    this.pauseStartedAt = now
    this.ledger.suspend()
    this.expCounter.breakContinuity(now)
    this.expReadout.breakContinuity(now)
    this.expRateTracker.reset()
    this.rankTracker?.suspend()
    const record = this.update({ status: 'paused', pausedAt: now }, now)
    this.journeyRecorder.mark('pause', record, now)
    return record
  }

  resume(now = Date.now(), captureGeneration = this.captureGeneration + 1): SessionRecord {
    this.assertStatus('paused', 'interrupted')
    this.percentageProgress.breakContinuity()
    this.partialExp?.breakContinuity(now)
    this.expHistory.breakContinuity(now)
    const previousStatus = this.record!.status
    if (this.pauseStartedAt !== null) this.accumulatedPauseMs += Math.max(0, now - this.pauseStartedAt)
    this.pauseStartedAt = null
    this.mapTracker.observe(this.activeDuration(now), null)
    this.mapBoundaryAt = now
    this.captureGeneration = captureGeneration
    this.ledger.resume(now, captureGeneration)
    this.expCounter.breakContinuity(now)
    this.expReadout.breakContinuity(now)
    this.expRateTracker.reset()
    const record = this.update({ status: 'running', pausedAt: undefined }, now)
    this.journeyRecorder.mark(previousStatus === 'interrupted' ? 'capture-restored' : 'resume', record, now)
    return record
  }

  interrupt(now = Date.now()): SessionRecord {
    this.assertStatus('running')
    this.percentageProgress.breakContinuity()
    this.partialExp?.breakContinuity(now)
    this.expHistory.breakContinuity(now)
    this.pauseStartedAt = now
    this.ledger.suspend()
    this.expCounter.breakContinuity(now)
    this.expReadout.breakContinuity(now)
    this.expRateTracker.reset()
    this.rankTracker?.suspend()
    const record = this.update({ status: 'interrupted', pausedAt: now }, now)
    this.journeyRecorder.mark('capture-interrupted', record, now)
    return record
  }

  ingest(
    state: GameStateSnapshot,
    events: readonly GameEvent[],
    observation?: ValidatedObservation,
    captureGeneration = this.captureGeneration,
  ): SessionRecord {
    return this.ingestWithAccounting(state, events, observation, captureGeneration).record
  }

  reconnectCapture(now: number, captureGeneration: number): SessionRecord {
    this.assertStatus('running')
    this.mapTracker.observe(this.activeDuration(now), null)
    this.mapBoundaryAt = now
    this.percentageProgress.breakContinuity()
    this.partialExp?.breakContinuity(now)
    this.expHistory.breakContinuity(now)
    this.expCounter.reconnectCapture(now)
    this.expReadout.breakContinuity(now)
    this.expRateTracker.reset()
    this.journeyRecorder.breakContinuity()
    // The trusted ledger still sees the new generation and excludes unobserved intervals.
    this.captureGeneration = captureGeneration
    return this.update({}, now)
  }

  ingestWithAccounting(
    state: GameStateSnapshot,
    events: readonly GameEvent[],
    observation?: ValidatedObservation,
    captureGeneration = this.captureGeneration,
    minimumConfidence = .62,
  ): SessionIngestResult {
    this.assertStatus('running')
    if (this.captureGeneration !== captureGeneration) {
      this.mapTracker.observe(this.activeDuration(state.capturedAt), null)
      this.mapBoundaryAt = state.capturedAt
      this.percentageProgress.breakContinuity()
      this.partialExp?.breakContinuity(state.capturedAt)
      this.expHistory.breakContinuity(state.capturedAt)
      this.expCounter.invalidateContinuity(state.capturedAt)
      this.expReadout.breakContinuity(state.capturedAt)
      this.expRateTracker.reset()
    }
    this.captureGeneration = captureGeneration
    const activeAt = this.activeDuration(state.capturedAt)
    const mapAt = observation?.mapName.capturedAt ?? state.observedAt?.mapName ?? state.capturedAt
    if ((observation ? observation.accepted.mapName : state.mapName !== null) && mapAt !== null && mapAt >= this.mapBoundaryAt) {
      this.mapTracker.observe(activeAt, observation ? observation.mapName.value : state.mapName)
      this.mapBoundaryAt = mapAt
    }
    let expState = state, expObservation = observation
    const unknown = this.partialExp?.snapshot().waitingForLevel
    if (unknown) {
      const candidate = observation?.expExactCandidate
      const levelConfirmed = observation?.accepted.level && observation.level.confirmation === 'consensus'
        && observation.level.value === candidate?.level
      const recovered = this.partialExp!.observe(candidate && !levelConfirmed ? { ...candidate, level: null, levelSourceAt: null } : candidate,
        state.capturedAt, minimumConfidence)
      if (recovered) {
        expState = { ...state, level: recovered.level, exp: recovered.value,
          observedAt: { ...state.observedAt!, level: recovered.levelSourceAt, exp: recovered.sourceAt } }
        this.knownExpStartedActiveAt = this.activeDuration(recovered.sourceAt)
        this.ledger.seedExpBaseline(recovered.sourceAt, expState, this.knownExpStartedActiveAt,
          observation?.expCandidateAdmission?.accounting?.sourceAt === recovered.sourceAt ? observation.expCandidateAdmission : observation?.expEvidenceAdmission)
        this.expCounter.reset(expState, recovered.sourceAt)
        this.expReadout.reset()
        this.rankTracker?.reset()
        // Both paths share this endpoint. The handoff frame cannot credit it twice.
        expObservation = undefined
      } else {
        expState = { ...state, level: null, exp: { absolute: null, ratio: null } }
        expObservation = observation ? { ...observation, expExactCandidate: undefined, expExactEvidence: undefined,
          expExactEvidenceAccepted: false, accepted: { ...observation.accepted, exp: false, level: false },
          level: { ...observation.level, value: null, availability: 'unavailable' },
          exp: { ...observation.exp, value: null, availability: 'unavailable' } } : undefined
      }
    }
    const percentageRankingGap = this.preset.id === 'gcw' && (expObservation?.expPercentCandidate !== undefined || expState.exp.estimated === true)
    if (percentageRankingGap || this.percentageRankingGap) {
      this.rankTracker?.reset()
      this.ledger.breakExpContinuity(state.capturedAt)
    }
    this.percentageRankingGap = percentageRankingGap
    const accounting = this.ledger.ingest(expState, unknown ? [] : events, expObservation, activeAt, captureGeneration)
    const percentEvidence = expObservation?.expEstimatedEvidence
    if (percentEvidence && expObservation?.expEstimatedAdmission?.status === 'accepted') {
      if (percentEvidence.value.absolute === null && !this.percentageProgress.snapshot() && this.expCounter.snapshot().point) this.expCounter.invalidateContinuity(percentEvidence.sourceAt)
      this.percentageProgress.observe(percentEvidence, expObservation.expEstimatedAdmission.transition === 'rebaseline')
    } else if (expObservation?.accepted.exp && !expState.exp.percentageOnly) this.percentageProgress.reset()
    const wasIncomplete = this.expCounter.isIncomplete()
    if (expObservation) this.expCounter.ingestObservation(expObservation, minimumConfidence)
    else this.expCounter.ingestState(expState)
    this.expCounter.confirmLevelUp(unknown ? [] : events)
    this.expReadout.ingest(this.expCounter, expState, expObservation, unknown ? [] : events, minimumConfidence, at => this.activeDuration(at))
    if (wasIncomplete && !this.expCounter.isIncomplete()) this.journeyRecorder.breakContinuity()
    const change = this.expCounter.snapshot().journeyTransition
    if (change?.id && change.status === 'confirmed') {
      if (change.kind === 'loss') this.confirmedLosses.add(change.id)
      else if (change.kind === 'level-up' && (change.revision ?? 0) > 1) this.confirmedLosses.delete(change.id)
    }
    if (this.confirmedLosses.size) {
      if (!this.record!.rankIneligibleReason) this.record = { ...this.record!, rankIneligibleReason: 'confirmed-exp-loss' }
      this.pendingRankSubmissions = []
      this.rankTracker?.reset()
    } else if (this.record!.rankIneligibleReason === 'confirmed-exp-loss') {
      this.record = { ...this.record!, rankIneligibleReason: this.rankSourceRestricted ?? (this.rankPerformanceRestricted ? 'low-performance-tier' : undefined) }
      this.rankTracker?.reset()
    }
    this.latestAccounting = accounting
    const startState = this.record!.startState ?? state
    this.latestState = state
    const record = this.update({ startState, endState: state }, state.capturedAt)
    this.journeyRecorder.ingest(record, accounting, unknown ? events.filter(event => event.type !== 'level-up') : events, state, this.expReadout.journeyDisplay(this.expCounter))
    const completedRankSegment = record.rankIneligibleReason || unknown || percentageRankingGap ? null : this.rankTracker?.ingest(state, accounting)
    if (completedRankSegment) this.pendingRankSubmissions.push(completedRankSegment)
    return { record, accounting }
  }

  end(now = Date.now()): SessionRecord {
    this.assertStatus('running', 'paused', 'interrupted')
    if (this.pauseStartedAt !== null) this.accumulatedPauseMs += Math.max(0, now - this.pauseStartedAt)
    this.pauseStartedAt = null
    this.expCounter.closePending(now)
    this.expReadout.finish(this.expCounter, now)
    this.expRateTracker.reset()
    const record = this.update({ status: 'ended', endedAt: now, pausedAt: undefined }, now)
    this.journeyRecorder.finish(record, now)
    const completedRankSegment = record.rankIneligibleReason ? null : this.rankTracker?.finish(now)
    if (completedRankSegment) this.pendingRankSubmissions.push(completedRankSegment)
    return record
  }

  current(now = Date.now()): SessionRecord | null {
    if (!this.record) return null
    this.expCounter.expireJourney(now)
    this.journeyRecorder.expirePending(now)
    return this.update({}, now)
  }

  liveRates(now = this.latestState?.capturedAt ?? Date.now()): LiveRateSnapshot {
    this.expCounter.expireJourney(now)
    const trusted = this.ledger.liveRates(now)
    const durationMs = this.currentActiveDuration(now)
    const partialExp = this.partialStatistics(durationMs)
    const gained = partialExp?.gained ?? this.expCounter.total()
    const rateDuration = partialExp?.durationMs ?? durationMs
    const average = this.expPerHour(gained, rateDuration)
    const expChart = this.expHistory.snapshot(this.record?.endedAt ?? now, this.expCounter.isIncomplete() ? null : average, this.record?.status ?? 'idle')
    const readout = this.expReadout.snapshot(this.expCounter, now, this.record?.status === 'running', durationMs)
    return {
      ...trusted,
      ...(partialExp ? { partialExp } : {}),
      expChart: this.percentageProgress.chart(now) ?? { ...expChart, approximate: this.expCounter.approximate() },
      ...(this.percentageProgress.snapshot() ? { percentageExp: this.percentageProgress.snapshot() } : {}),
      ...readout,
      expRealtime: this.expRateTracker.snapshot(expChart.recent, average, this.expPerHour(gained, this.activeDuration(expChart.recent.at ?? now)),
        this.expCounter.snapshot().status, this.record?.status === 'running' && readout.expReadout?.scope !== 'recovery'),
      expPerHour: {
        value: this.expPerHour(gained, rateDuration),
        calculationStatus: this.expCounter.snapshot().status,
        approximate: this.expCounter.approximate(),
        status: gained === null ? 'unavailable' : 'ready',
        activeDurationMs: rateDuration,
        updatedAt: this.expCounter.latestSourceAt(),
      },
    }
  }

  accountingFrame(): AccountingFrame | null {
    return this.latestAccounting
  }

  expDisplayStatus() {
    return this.expCounter.snapshot()
  }

  journey(): SessionJourney | null {
    return this.journeyRecorder.snapshot()
  }

  restrictRankForPerformance(tier: PerformanceTier, captureFps: number | null = null): SessionRecord | null {
    if (!this.record || this.record.status === 'ended') return this.record
    // A low-rate shared stream remains low-rate until the next share, even after changing tiers.
    if (tier !== 'low' && tier !== 'medium' && !(captureFps !== null && captureFps > 0 && captureFps < 1)) return this.record
    this.rankPerformanceRestricted = true
    this.record = { ...this.record, rankIneligibleReason: this.rankSourceRestricted ?? 'low-performance-tier' }
    this.pendingRankSubmissions = []
    this.rankTracker?.reset()
    return this.record
  }

  restrictRankForSource(reason: RankSourceRestriction | null): SessionRecord | null {
    if (!reason || !this.record || this.record.status === 'ended') return this.record
    this.rankSourceRestricted ??= reason
    this.record = { ...this.record, rankIneligibleReason: this.rankSourceRestricted }
    this.pendingRankSubmissions = []
    this.rankTracker?.reset()
    return this.record
  }

  setExpSamplingInterval(intervalMs: number): void {
    this.percentageProgress.setSamplingInterval(intervalMs)
    this.partialExp?.setSamplingInterval(intervalMs)
    this.expHistory.setSamplingInterval(intervalMs)
  }

  setExpReadoutInterval(intervalMs: number): void {
    this.expReadout.setSamplingInterval(intervalMs)
  }

  rankSubmissions(): readonly RankSubmissionDraft[] {
    return this.record?.rankIneligibleReason ? [] : [...this.pendingRankSubmissions]
  }

  private update(patch: Partial<SessionRecord>, now: number): SessionRecord {
    if (!this.record) throw new Error('No active record')
    const activeDurationMs = this.currentActiveDuration(now)
    const next = { ...this.record, ...patch, activeDurationMs, mapSummary: this.mapTracker.snapshot(activeDurationMs) }
    this.record = { ...next, aggregate: this.aggregate(next, activeDurationMs) }
    return this.record
  }

  private activeDuration(now: number): number {
    if (!this.record) return 0
    const effectiveNow = this.record.status === 'ended' && this.record.endedAt !== undefined
      ? Math.min(now, this.record.endedAt)
      : now
    const currentPause = this.pauseStartedAt === null ? 0 : Math.max(0, effectiveNow - this.pauseStartedAt)
    return Math.max(0, effectiveNow - this.record.startedAt - this.accumulatedPauseMs - currentPause)
  }

  private currentActiveDuration(now: number): number {
    return Math.max(this.record?.activeDurationMs ?? 0, this.activeDuration(now))
  }

  private aggregate(record: SessionRecord, durationMs: number): SessionAggregate {
    const end = this.latestState ?? record.endState
    const trustedAggregate = this.ledger.aggregate()
    const partialExp = this.partialStatistics(durationMs)
    const expGained = partialExp?.gained ?? this.expCounter.total()
    return {
      durationMs,
      ...trustedAggregate,
      ...(this.percentageProgress.snapshot() ? { percentageExp: this.percentageProgress.snapshot() } : {}),
      ...(partialExp ? { partialExp } : {}),
      expGained,
      expDisplayStatus: this.expCounter.snapshot().status,
      expGainedApproximate: this.expCounter.approximate(),
      expRateGained: trustedAggregate.expGained,
      expPerHour: this.expPerHour(expGained, partialExp?.durationMs ?? durationMs),
      coverage: end?.coverage ?? emptyCoverage(),
    }
  }

  private partialStatistics(durationMs: number) {
    const recovered = this.expReadout.partialStatistics(this.expCounter)
    if (!this.partialExp) return recovered
    const partial = this.partialExp.snapshot()
    if (recovered) return { ...partial, gained: partial.gained + recovered.gained,
      durationMs: partial.durationMs + recovered.durationMs,
      segments: [...partial.segments, ...recovered.segments], gaps: [...partial.gaps, ...recovered.gaps] }
    const display = this.expCounter.snapshot()
    if (this.knownExpStartedActiveAt !== null && display.status === 'ready' && display.netGain !== null) {
      this.partialKnownGain = display.netGain
      this.partialKnownDuration = Math.max(this.partialKnownDuration, Math.min(durationMs, this.activeDuration(display.sourceAt ?? 0)) - this.knownExpStartedActiveAt)
    }
    return { ...partial, gained: partial.gained + this.partialKnownGain,
      durationMs: partial.durationMs + this.partialKnownDuration }
  }

  private emptyAggregate(): SessionAggregate {
    return {
      durationMs: 0,
      expGained: null,
      expRateGained: null,
      expTrustedDurationMs: 0,
      expTrustedPerHour: null,
      expPerHour: null,
      mesosStartBalance: null,
      mesosCurrentBalance: null,
      mesosBalanceChange: null,
      mesosDelta: null,
      mesosTrustedDurationMs: 0,
      mesosPerHour: null,
      coverage: emptyCoverage(),
    }
  }

  private expPerHour(gained: number | null, durationMs: number): number | null {
    if (gained === null) return null
    const value = durationMs > 0 ? gained / durationMs * HOUR_MS : 0
    return Number.isFinite(value) ? value : null
  }

  private assertStatus(...allowed: SessionRecord['status'][]): void {
    if (!this.record || !allowed.includes(this.record.status)) throw new Error(`Invalid record transition from ${this.record?.status ?? 'none'}`)
  }
}
